/* ============================================
   UN.BASED — Theme Animations & Interactions
   ============================================
   Dependencies (loaded via CDN in theme.liquid):
   - GSAP 3.12.5 + ScrollTrigger + ScrollToPlugin
   - SplitType 0.3.4 (text splitting)
   ============================================ */

(function() {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ubPostLoaderInitDone = false;
  var ubWaveMouse = { x: 0, y: 0 };
  var ubWaveMouseBound = false;

  function ensureWaveMouseTracking() {
    if (ubWaveMouseBound) return;
    ubWaveMouseBound = true;
    var mousePending = false;
    document.addEventListener(
      'mousemove',
      function (e) {
        if (mousePending) return;
        mousePending = true;
        window.requestAnimationFrame(function () {
          var w = Math.max(window.innerWidth, 1);
          var h = Math.max(window.innerHeight, 1);
          ubWaveMouse.x = (e.clientX / w) * 2 - 1;
          ubWaveMouse.y = (e.clientY / h) * 2 - 1;
          mousePending = false;
        });
      },
      { passive: true }
    );
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () {
        fn.apply(null, args);
      }, ms);
    };
  }

  // ============================================
  // GSAP SETUP
  // ============================================
  function initGSAP() {
    if (typeof gsap === 'undefined') return;
    if (typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);
    if (typeof ScrollToPlugin !== 'undefined') gsap.registerPlugin(ScrollToPlugin);
  }

  // ============================================
  // LOADER — Animated Count + Slide Out
  // ============================================
  function initLoader() {
    var loader = document.getElementById('ub-loader');
    if (!loader) return;

    // Fast mode: no loader animation — show content immediately for all visitors
    loader.style.display = 'none';
    sessionStorage.setItem('ub_s', '1');
    initAfterLoader();
    return;

    var counter = document.getElementById('ub-counter');
    var barFill = loader.querySelector('.ub-loader-bar-fill');

    if (prefersReducedMotion) {
      loader.style.display = 'none';
      initAfterLoader();
      return;
    }

    // Skip loader for return visits within the same session
    if (sessionStorage.getItem('ub_s')) {
      loader.style.display = 'none';
      initAfterLoader();
      return;
    }

    // Skip loader for external referrals (Instagram, Google, social media, email, UTM links).
    // These visitors need to see content instantly — they didn't choose to watch a loader.
    var _ref = document.referrer;
    var _isExternal = (_ref && (function() { try { return new URL(_ref).origin !== window.location.origin; } catch(e) { return false; } })());
    var _hasUtm = /[?&]utm_/.test(window.location.search);
    var _isIgBrowser = /Instagram/.test(navigator.userAgent);
    if (_isExternal || _hasUtm || _isIgBrowser) {
      sessionStorage.setItem('ub_s', '1');
      loader.style.display = 'none';
      initAfterLoader();
      return;
    }

    sessionStorage.setItem('ub_s', '1');

    var count = 0;
    var duration = 400;
    var startTime = Date.now();

    var countInterval = setInterval(function() {
      var elapsed = Date.now() - startTime;
      var progress = Math.min((elapsed / duration) * 100, 100);

      count = Math.floor(progress);
      if (counter) counter.textContent = count;
      if (barFill) barFill.style.width = count + '%';

      if (count >= 100) {
        clearInterval(countInterval);

        if (typeof gsap !== 'undefined') {
          try {
            gsap.to(loader, {
              y: '-100vh',
              duration: 0.5,
              ease: 'power2.inOut',
              delay: 0,
              onComplete: function() {
                loader.style.display = 'none';
                initAfterLoader();
              }
            });
          } catch(e) {
            loader.style.display = 'none';
            initAfterLoader();
          }
        } else {
          setTimeout(function() {
            loader.style.display = 'none';
            initAfterLoader();
          }, 400);
        }
      }
    }, 30);
  }

  // ============================================
  // PAGE FADE — No full-screen loader (Theme settings → Loading screen)
  // ============================================
  function initPageFade() {
    var root = document.documentElement;
    if (!root.classList.contains('ub-page-fade-pending')) return;

    // Fast mode: no fade animation
    root.classList.remove('ub-page-fade-pending');
    document.body.style.removeProperty('opacity');
    sessionStorage.setItem('ub_s', '1');
    initAfterLoader();
    return;

    function finish() {
      root.classList.remove('ub-page-fade-pending');
      if (typeof gsap !== 'undefined') {
        try {
          gsap.set(document.body, { clearProps: 'opacity' });
        } catch (e) {
          document.body.style.removeProperty('opacity');
        }
      } else {
        document.body.style.removeProperty('opacity');
      }
      initAfterLoader();
    }

    var _ref2 = document.referrer;
    var _isExternal2 = (_ref2 && (function() { try { return new URL(_ref2).origin !== window.location.origin; } catch(e) { return false; } })());
    var _hasUtm2 = /[?&]utm_/.test(window.location.search);
    var _isIgBrowser2 = /Instagram/.test(navigator.userAgent);
    if (
      prefersReducedMotion ||
      (window.Shopify && window.Shopify.designMode) ||
      root.classList.contains('shopify-design-mode') ||
      sessionStorage.getItem('ub_s') ||
      _isExternal2 || _hasUtm2 || _isIgBrowser2
    ) {
      if (!sessionStorage.getItem('ub_s')) sessionStorage.setItem('ub_s', '1');
      finish();
      return;
    }
    sessionStorage.setItem('ub_s', '1');

    if (typeof gsap !== 'undefined') {
      try {
        gsap.to(document.body, {
          opacity: 1,
          duration: 0.85,
          ease: 'power2.out',
          delay: 0.05,
          onComplete: finish,
        });
      } catch (e) {
        finish();
      }
    } else {
      document.body.style.opacity = '1';
      finish();
    }
  }

  // ============================================
  // POST-LOADER INIT
  // ============================================
  function initAfterLoader() {
    if (ubPostLoaderInitDone) return;
    ubPostLoaderInitDone = true;

    // Fast mode: skip all scroll/entry animations, reveal everything immediately
    revealAllImmediate();
    clearScrollAnimationFallback();
    clearProductCardsFallback();
  }

  // ============================================
  // HERO TITLE — SplitType word reveal (words only: avoids line re-wrap vs per-char)
  // ============================================
  function initHeroTitle() {
    if (typeof gsap === 'undefined' || typeof SplitType === 'undefined' || prefersReducedMotion) return;

    var heroTitle = document.querySelector('.ub-hero-title');
    if (!heroTitle || !heroTitle.textContent.trim()) return;

    try {
      var split = new SplitType(heroTitle, { types: 'words' });
      if (!split.words || !split.words.length) return;

      gsap.from(split.words, {
        opacity: 0,
        y: 36,
        stagger: 0.055,
        duration: 0.88,
        ease: 'power4.out',
        delay: 0.12,
        force3D: true,
      });
    } catch(e) {
      heroTitle.style.opacity = '1';
      heroTitle.querySelectorAll('.word').forEach(function(w) {
        w.style.opacity = '1';
        w.style.transform = 'none';
      });
    }
  }

  // ============================================
  // HERO SEQUENCE — Staggered Element Reveal
  // ============================================
  function initHeroSequence() {
    if (typeof gsap === 'undefined' || prefersReducedMotion) return;

    var elements = [
      '.ub-hero-overline',
      '.ub-hero-subtitle',
      '.ub-meta-stats',
      '.ub-hero-cta',
      '.ub-scroll-indicator'
    ];

    try {
      var tl = gsap.timeline({ delay: 0.55 });

      elements.forEach(function(sel) {
        var el = document.querySelector('.ub-hero ' + sel);
        if (el) {
          tl.from(el, {
            opacity: 0,
            y: 26,
            duration: 0.72,
            ease: 'power3.out',
          }, '-=0.38');
        }
      });
    } catch (e) {
      elements.forEach(function(sel) {
        var el = document.querySelector('.ub-hero ' + sel);
        if (el) {
          el.style.opacity = '1';
          el.style.transform = 'none';
        }
      });
    }
  }

  // ============================================
  // SCROLL-TRIGGERED ANIMATIONS
  // ============================================
  function clearProductCardsFallback() {
    document.querySelectorAll('.ub-product-card').forEach(function(el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  function revealAllImmediate() {
    document.querySelectorAll('[data-ub-reveal]').forEach(function(el) {
      el.classList.add('revealed');
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.transition = 'none';
      if (el.getAttribute('data-ub-reveal') === 'stagger') {
        Array.from(el.children).forEach(function(child) {
          child.style.opacity = '1';
          child.style.transform = 'none';
          child.style.transition = 'none';
        });
      }
    });
  }

  function clearScrollAnimationFallback() {
    var sel =
      '.ub-trust-card, .ub-trust-badges__card, .ub-deliverable-card, .ub-feature-card, .ub-theme-card, .ub-video-card, .ub-process-step, .ub-comparison-row:not(.ub-comparison-header), .ub-section-title, .ub-trust-badges__heading, .ub-builds-container, .ub-cta-content, .ub-system-visual, .ub-compliance-title, .ub-benefits-bar__item';
    document.querySelectorAll(sel).forEach(function(el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    document.querySelectorAll('.ub-hero-title .word, .ub-section-title .word, .ub-page-hero-title .word').forEach(function(w) {
      w.style.opacity = '1';
      w.style.transform = 'none';
    });
  }

  function initScrollAnimations() {
    var isProductPage = document.body.classList.contains('ub-template-product');

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined' || prefersReducedMotion) {
      clearScrollAnimationFallback();
      clearProductCardsFallback();
      return;
    }

    try {
      /* ── Product page feature animations ── */
      if (isProductPage) {
        clearScrollAnimationFallback();

        // Feature split rows — media slides in from the side
        gsap.utils.toArray('.ub-pdp-split-row').forEach(function(row) {
          var media = row.querySelector('.ub-pdp-split-row__media');
          var copy = row.querySelector('.ub-pdp-split-row__copy');
          var isRight = row.classList.contains('ub-pdp-split-row--media-right');

          if (media) {
            gsap.from(media, {
              scrollTrigger: { trigger: row, start: 'top 82%', toggleActions: 'play none none none' },
              opacity: 0,
              x: isRight ? 80 : -80,
              scale: 0.94,
              duration: 1,
              ease: 'power3.out',
            });
          }
          if (copy) {
            gsap.from(copy, {
              scrollTrigger: { trigger: row, start: 'top 82%', toggleActions: 'play none none none' },
              opacity: 0,
              y: 40,
              duration: 0.8,
              delay: 0.15,
              ease: 'power3.out',
            });
          }
        });

        // Feature trio cards — stagger fade-up with scale
        gsap.utils.toArray('.ub-pdp-trio__card').forEach(function(card, i) {
          gsap.from(card, {
            scrollTrigger: { trigger: card, start: 'top 85%', toggleActions: 'play none none none' },
            opacity: 0,
            y: 50,
            scale: 0.95,
            duration: 0.8,
            delay: i * 0.12,
            ease: 'power3.out',
          });
        });

        // Feature grid cards — stagger with subtle rotation
        gsap.utils.toArray('.ub-feature-card').forEach(function(card, i) {
          gsap.from(card, {
            scrollTrigger: { trigger: card, start: 'top 88%', toggleActions: 'play none none none' },
            opacity: 0,
            y: 35,
            scale: 0.96,
            duration: 0.7,
            delay: (i % 3) * 0.1,
            ease: 'power3.out',
          });
        });

        // Deliverable cards — cascade in
        gsap.utils.toArray('.ub-deliverable-card').forEach(function(card, i) {
          gsap.from(card, {
            scrollTrigger: { trigger: card, start: 'top 88%', toggleActions: 'play none none none' },
            opacity: 0,
            y: 30,
            duration: 0.6,
            delay: i * 0.08,
            ease: 'power2.out',
          });
        });

        // Section titles on product pages — fade up
        gsap.utils.toArray('.ub-pdp-feature-split__header, .ub-pdp-trio__eyebrow, .ub-pdp-trio__title, .ub-pdp-trio__subtitle').forEach(function(el) {
          gsap.from(el, {
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
            opacity: 0,
            y: 25,
            duration: 0.7,
            ease: 'power2.out',
          });
        });

        // Parallax float on feature split media images as user scrolls
        gsap.utils.toArray('.ub-pdp-split-row__img').forEach(function(img) {
          gsap.to(img, {
            scrollTrigger: {
              trigger: img.closest('.ub-pdp-split-row'),
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.2,
            },
            y: -30,
            ease: 'none',
          });
        });

        return;
      }

      // Cards: trust, deliverable, feature, theme — stagger within parent
      var cardSelectors = '.ub-trust-card, .ub-trust-badges__card, .ub-deliverable-card, .ub-feature-card, .ub-theme-card, .ub-video-card';
      gsap.utils.toArray(cardSelectors).forEach(function(card, i) {
        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
          opacity: 0,
          y: 40,
          duration: 0.7,
          delay: (i % 4) * 0.08,
          ease: 'power3.out',
        });
      });

      // Product cards — no GSAP animation; section is above-fold so immediate visibility is better.
      clearProductCardsFallback();

      // Process steps — entrance only (no CSS [data-ub-reveal] stagger: it fought GSAP + hover)
      gsap.utils.toArray('.ub-process-step').forEach(function(step, i) {
        gsap.from(step, {
          scrollTrigger: {
            trigger: step,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
          opacity: 0,
          y: 30,
          scale: 0.96,
          duration: 0.7,
          delay: i * 0.12,
          ease: 'power3.out',
          onComplete: function () {
            gsap.set(step, { clearProps: 'opacity,transform' });
          },
        });
      });

      // Section titles — word split reveal (strip CSS reveal on parent: opacity:0 hid SplitType words)
      if (typeof SplitType !== 'undefined') {
        var titleDesignMode =
          (window.Shopify && window.Shopify.designMode) ||
          document.documentElement.classList.contains('shopify-design-mode');
        gsap.utils.toArray('.ub-section-title').forEach(function (title) {
          if (!title.textContent.trim()) return;
          if (titleDesignMode) {
            title.classList.add('revealed');
            title.removeAttribute('data-ub-reveal');
            title.style.opacity = '';
            title.style.transform = '';
            return;
          }
          try {
            title.classList.add('revealed');
            title.removeAttribute('data-ub-reveal');
            title.style.opacity = '';
            title.style.transform = '';
            var split = new SplitType(title, { types: 'words' });
            if (!split.words || !split.words.length) return;
            gsap.from(split.words, {
              scrollTrigger: {
                trigger: title,
                start: 'top 85%',
                toggleActions: 'play none none none',
              },
              opacity: 0,
              y: 18,
              stagger: 0.04,
              duration: 0.6,
              ease: 'power2.out',
              force3D: true,
            });
          } catch (e) {
            title.style.opacity = '1';
            title.querySelectorAll('.word').forEach(function (w) {
              w.style.opacity = '1';
              w.style.transform = 'none';
            });
          }
        });
      }

      // Page hero title — large text word reveal
      if (typeof SplitType !== 'undefined') {
        var pageTitle = document.querySelector('.ub-page-hero-title');
        if (pageTitle && pageTitle.textContent.trim()) {
          try {
            var pageSplit = new SplitType(pageTitle, { types: 'words' });
            if (pageSplit.words && pageSplit.words.length) {
              gsap.from(pageSplit.words, {
                opacity: 0,
                y: 24,
                stagger: 0.04,
                duration: 0.7,
                ease: 'power3.out',
                delay: 0.3,
                force3D: true,
              });
            }
          } catch(e) {
            pageTitle.style.opacity = '1';
            pageTitle.querySelectorAll('.word').forEach(function(w) {
              w.style.opacity = '1';
              w.style.transform = 'none';
            });
          }
        }
      }

      // Comparison rows — slide from left
      gsap.utils.toArray('.ub-comparison-row:not(.ub-comparison-header)').forEach(function(row, i) {
        gsap.from(row, {
          scrollTrigger: {
            trigger: row,
            start: 'top 92%',
            toggleActions: 'play none none none',
          },
          opacity: 0,
          x: -20,
          duration: 0.5,
          delay: i * 0.05,
          ease: 'power2.out',
        });
      });

      // Builds container scale-in
      var builds = document.querySelector('.ub-builds-container');
      if (builds) {
        gsap.from(builds, {
          scrollTrigger: {
            trigger: builds,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
          opacity: 0,
          y: 30,
          scale: 0.98,
          duration: 0.8,
          ease: 'power3.out',
        });
      }

      /* UN.BASED CTA: do not gsap.from the whole .ub-cta-content — it forces opacity 0 until
         ScrollTrigger runs; if ST misses or layout shifts, the entire block stays invisible.
         Children already use [data-ub-reveal] + IntersectionObserver. */

    } catch(e) {
      clearScrollAnimationFallback();
      clearProductCardsFallback();
    }

    window.addEventListener('load', function() {
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    }, { once: true });

    window.addEventListener(
      'resize',
      debounce(function () {
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      }, 200)
    );
  }

  // ============================================
  // PARALLAX — Subtle depth on scroll
  // ============================================
  function initParallax() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined' || prefersReducedMotion) return;

    // Hero gradient orbs float on scroll
    var hero = document.querySelector('.ub-hero');
    if (hero) {
      gsap.to(hero, {
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.5,
        },
        '--hero-shift': '80px',
        ease: 'none',
      });
    }

    // System visual parallax
    gsap.utils.toArray('.ub-system-visual').forEach(function(el) {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        scale: 0.95,
        duration: 0.8,
        ease: 'power3.out',
      });
    });

    // Compliance banner text scale
    var compTitle = document.querySelector('.ub-compliance-title');
    if (compTitle) {
      gsap.from(compTitle, {
        scrollTrigger: {
          trigger: compTitle,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        opacity: 0,
        scale: 0.98,
        duration: 0.9,
        ease: 'power3.out',
        force3D: true,
      });
    }
  }

  // ============================================
  // COUNTER ANIMATIONS — Animate stat numbers
  // ============================================
  function initCounterAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined' || prefersReducedMotion) return;

    gsap.utils.toArray('.ub-meta-stat-value').forEach(function(el) {
      var text = el.textContent.trim();
      // Check if it starts with a number
      var match = text.match(/^([\d,]+\.?\d*)(.*)/);
      if (!match) return;

      var numStr = match[1].replace(/,/g, '');
      var suffix = match[2];
      var target = parseFloat(numStr);
      if (isNaN(target)) return;

      var hasComma = match[1].indexOf(',') !== -1;
      var hasDecimal = match[1].indexOf('.') !== -1;

      el.textContent = '0' + suffix;

      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: function() {
          gsap.to({ val: 0 }, {
            val: target,
            duration: 1.5,
            ease: 'power2.out',
            onUpdate: function() {
              var current = this.targets()[0].val;
              if (hasDecimal) {
                current = current.toFixed(1);
              } else {
                current = Math.floor(current);
              }
              if (hasComma) {
                current = Number(current).toLocaleString();
              }
              el.textContent = current + suffix;
            }
          });
        }
      });
    });
  }

  // ============================================
  // REVEAL ANIMATIONS (IntersectionObserver)
  // Runs after loader so the viewport isn’t covered; includes fallbacks if IO misses.
  // ============================================
  function revealElementNow(el, forceImmediate) {
    if (!el || el.classList.contains('revealed')) return;
    el.classList.add('revealed');
    if (el.getAttribute('data-ub-reveal') === 'stagger') {
      Array.from(el.children).forEach(function(child, i) {
        child.style.transitionDelay = (i * 0.1) + 's';
      });
    }
    if (forceImmediate) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      if (el.getAttribute('data-ub-reveal') === 'stagger') {
        Array.from(el.children).forEach(function(child) {
          child.style.opacity = '1';
          child.style.transform = 'none';
        });
      }
    }
  }

  function elementIsVisibleInViewport(el) {
    var rect = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < vh && rect.bottom > 0 && rect.left < (window.innerWidth || 0) && rect.right > 0;
  }

  function scanRevealInView() {
    document.querySelectorAll('[data-ub-reveal]:not(.revealed)').forEach(function(el) {
      if (elementIsVisibleInViewport(el)) {
        revealElementNow(el, false);
      }
    });
  }

  function initRevealAnimations() {
    /* Exclude .ub-section-title: GSAP SplitType animates words; IO + .revealed would show full
       headline first, then gsap.from hides words — visible flicker / “disappearing” copy */
    var revealElements = document.querySelectorAll('[data-ub-reveal]:not(.ub-section-title)');
    if (!revealElements.length) return;

    var designMode =
      (window.Shopify && window.Shopify.designMode) ||
      document.documentElement.classList.contains('shopify-design-mode');

    if (prefersReducedMotion || designMode) {
      revealElements.forEach(function (el) {
        revealElementNow(el, true);
      });
      document.querySelectorAll('.ub-section-title[data-ub-reveal]').forEach(function (el) {
        revealElementNow(el, true);
      });
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      revealElements.forEach(function(el) {
        revealElementNow(el, true);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var el = entry.target;
            revealElementNow(el, false);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.01, rootMargin: '0px 0px 12% 0px' }
    );

    revealElements.forEach(function(el) {
      observer.observe(el);
    });

    /* Elements already in view (common on first paint after loader) — IO can miss the first frame */
    requestAnimationFrame(function() {
      scanRevealInView();
      requestAnimationFrame(scanRevealInView);
    });

    window.addEventListener(
      'load',
      function() {
        scanRevealInView();
      },
      { once: true }
    );

    /* Last resort: never leave copy invisible */
    window.setTimeout(function() {
      document.querySelectorAll('[data-ub-reveal]:not(.revealed)').forEach(function(el) {
        revealElementNow(el, true);
      });
    }, 2500);

    /* Mobile Safari: returning from another tab / bfcache can leave stagger children at opacity 0 */
    function resumeUbUiAfterDocumentVisible() {
      if (document.visibilityState !== 'visible') return;
      scanRevealInView();
      document.querySelectorAll('[data-ub-reveal="stagger"].revealed').forEach(function (el) {
        Array.from(el.children).forEach(function (child) {
          child.style.opacity = '1';
          child.style.transform = 'none';
        });
      });
      document.querySelectorAll('[data-ub-video-carousel-viewport]').forEach(function (vp) {
        var sl = vp.scrollLeft;
        vp.scrollLeft = sl ? sl - 1 : 1;
        vp.scrollLeft = sl;
      });
    }

    document.addEventListener('visibilitychange', resumeUbUiAfterDocumentVisible);
    window.addEventListener('pageshow', function (ev) {
      if (ev.persisted) resumeUbUiAfterDocumentVisible();
    });
  }

  // ============================================
  // FAQ ACCORDION
  // ============================================
  var ubFaqDelegated = false;

  function initFAQ() {
    if (ubFaqDelegated) return;
    ubFaqDelegated = true;
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('.ub-faq-toggle');
      if (!toggle) return;
      var item = toggle.closest('.ub-faq-item');
      if (!item) return;

      document.querySelectorAll('.ub-faq-item').forEach(function (otherItem) {
        if (otherItem !== item) otherItem.classList.remove('active');
      });
      item.classList.toggle('active');
    });
  }

  // ============================================
  // MARQUEE DUPLICATION
  // ============================================
  function initMarquees() {
    var marquees = document.querySelectorAll('.ub-marquee:not([data-ub-marquee-cloned]), .ub-brands-scroll:not([data-ub-marquee-cloned])');
    marquees.forEach(function(marquee) {
      if (!marquee) return;
      marquee.setAttribute('data-ub-marquee-cloned', 'true');
      var items = marquee.innerHTML;
      marquee.innerHTML += items;
    });
  }

  // ============================================
  // MAGNETIC BUTTONS
  // ============================================
  function initMagneticButtons() {
    if (prefersReducedMotion || typeof gsap === 'undefined') return;

    var buttons = document.querySelectorAll('.ub-btn');
    buttons.forEach(function(btn) {
      var btnRect = null;
      btn.addEventListener('mouseenter', function() {
        btnRect = btn.getBoundingClientRect();
      });
      btn.addEventListener('mousemove', function(e) {
        if (!btnRect) return;
        var x = e.clientX - btnRect.left - btnRect.width / 2;
        var y = e.clientY - btnRect.top - btnRect.height / 2;
        gsap.to(btn, {
          x: x * 0.15,
          y: y * 0.15,
          duration: 0.3,
          overwrite: 'auto',
        });
      });
      btn.addEventListener('mouseleave', function() {
        btnRect = null;
        gsap.to(btn, {
          x: 0,
          y: 0,
          duration: 0.4,
          ease: 'elastic.out(1, 0.5)',
          overwrite: 'auto',
        });
      });
    });
  }

  // ============================================
  // PRICING CARDS — horizontal scroll + prev/next (>2 plans)
  // ============================================
  function initPricingCardsCarousel() {
    document.querySelectorAll('[data-ub-pricing-carousel]').forEach(function (root) {
      if (root.getAttribute('data-ub-pricing-init') === '1') return;
      var viewport = root.querySelector('[data-ub-pricing-viewport]');
      var prevBtn = root.querySelector('.ub-pricing-cards__nav--prev');
      var nextBtn = root.querySelector('.ub-pricing-cards__nav--next');
      if (!viewport || !prevBtn || !nextBtn) return;

      root.setAttribute('data-ub-pricing-init', '1');

      function cells() {
        return viewport.querySelectorAll('.ub-pricing-cards__cell');
      }

      function currentIndex() {
        var list = cells();
        if (!list.length) return 0;
        var sl = viewport.scrollLeft;
        var best = 0;
        for (var i = 0; i < list.length; i++) {
          if (list[i].offsetLeft <= sl + 8) best = i;
          else break;
        }
        return best;
      }

      function updateNav() {
        var max = viewport.scrollWidth - viewport.clientWidth;
        var sl = viewport.scrollLeft;
        prevBtn.disabled = sl <= 1;
        nextBtn.disabled = max <= 1 || sl >= max - 1;
      }

      function go(delta) {
        var list = cells();
        if (!list.length) return;
        var i = Math.min(list.length - 1, Math.max(0, currentIndex() + delta));
        var el = list[i];
        viewport.scrollTo({
          left: el.offsetLeft,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
        window.setTimeout(updateNav, prefersReducedMotion ? 0 : 320);
      }

      prevBtn.addEventListener('click', function () {
        go(-1);
      });
      nextBtn.addEventListener('click', function () {
        go(1);
      });
      viewport.addEventListener('scroll', debounce(updateNav, 48), { passive: true });
      window.addEventListener('resize', debounce(updateNav, 120));
      updateNav();
    });
  }

  // ============================================
  // DEMO SLIDESHOW — per-panel screenshot nav
  // ============================================
  function initDemoSlideshowNav() {
    document.querySelectorAll('[data-ub-ds-slideshow]').forEach(function (root) {
      if (root.getAttribute('data-ub-ds-nav-init') === '1') return;
      root.setAttribute('data-ub-ds-nav-init', '1');

      function setupPanel(panel) {
        if (!panel || panel.getAttribute('data-ub-ds-panel-init') === '1') return;
        var track = panel.querySelector('.ub-ds__phone');
        var prevBtn = panel.querySelector('[data-ub-ds-nav="prev"]');
        var nextBtn = panel.querySelector('[data-ub-ds-nav="next"]');
        if (!track || !prevBtn || !nextBtn) return;
        panel.setAttribute('data-ub-ds-panel-init', '1');

        function getScrollStep() {
          var mq = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 989px)');
          var w = track.clientWidth;
          if (mq && mq.matches) {
            return Math.max(Math.floor(w), 1);
          }
          return Math.max(w, 1);
        }

        function applyCardSlideWidths() {
          var mq = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 989px)');
          var isMobile = mq && mq.matches;
          if (!isMobile) {
            track.querySelectorAll('.ub-ds__card').forEach(function (card) {
              card.style.removeProperty('flex');
              card.style.removeProperty('width');
              card.style.removeProperty('max-width');
            });
            return;
          }
          var w = getScrollStep();
          if (w < 2) return;
          track.querySelectorAll('.ub-ds__card').forEach(function (card) {
            card.style.flex = '0 0 ' + w + 'px';
            card.style.width = w + 'px';
            card.style.maxWidth = w + 'px';
          });
          var snapped = Math.round(track.scrollLeft / w) * w;
          if (Math.abs(track.scrollLeft - snapped) > 2) {
            track.scrollLeft = snapped;
          }
        }

        function bindTrackSlideWidths() {
          if (track.getAttribute('data-ub-ds-slide-layout') === '1') return;
          track.setAttribute('data-ub-ds-slide-layout', '1');
          var run = function () {
            window.requestAnimationFrame(applyCardSlideWidths);
          };
          if (typeof ResizeObserver !== 'undefined') {
            var ro = new ResizeObserver(run);
            ro.observe(track);
          }
          window.addEventListener('resize', debounce(run, 120));
          var mq = window.matchMedia('(max-width: 989px)');
          if (mq.addEventListener) {
            mq.addEventListener('change', run);
          } else if (mq.addListener) {
            mq.addListener(run);
          }
          run();
        }
        bindTrackSlideWidths();

        function cells() {
          return track.querySelectorAll('.ub-ds__card');
        }

        function currentIndex() {
          var list = cells();
          if (!list.length) return 0;
          var pageW = getScrollStep();
          return Math.max(0, Math.min(list.length - 1, Math.round(track.scrollLeft / pageW)));
        }

        function updateNav() {
          var list = cells();
          if (list.length <= 1) {
            prevBtn.disabled = true;
            nextBtn.disabled = true;
            return;
          }
          var idx = currentIndex();
          prevBtn.disabled = idx <= 0;
          nextBtn.disabled = idx >= list.length - 1;
        }

        function go(delta) {
          var list = cells();
          if (!list.length) return;
          var idx = Math.min(list.length - 1, Math.max(0, currentIndex() + delta));
          var pageW = getScrollStep();
          track.scrollTo({
            left: idx * pageW,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
          window.setTimeout(updateNav, prefersReducedMotion ? 0 : 320);
        }

        prevBtn.addEventListener('click', function () {
          go(-1);
        });
        nextBtn.addEventListener('click', function () {
          go(1);
        });
        track.addEventListener('scroll', debounce(updateNav, 48), { passive: true });
        window.addEventListener('resize', debounce(updateNav, 120));
        track.querySelectorAll('img.ub-ds__img').forEach(function (img) {
          function bumpLayout() {
            window.requestAnimationFrame(applyCardSlideWidths);
          }
          if (img.complete) bumpLayout();
          else img.addEventListener('load', bumpLayout, { once: true });
        });
        updateNav();
      }

      root.querySelectorAll('.ub-ds__panel').forEach(setupPanel);
      root.querySelectorAll('[data-ub-ld-tab]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          window.setTimeout(function () {
            root.querySelectorAll('.ub-ds__panel:not([hidden])').forEach(function (panel) {
              var track = panel.querySelector('.ub-ds__phone');
              if (track) track.scrollLeft = 0;
              var prevBtn = panel.querySelector('[data-ub-ds-nav="prev"]');
              var nextBtn = panel.querySelector('[data-ub-ds-nav="next"]');
              if (prevBtn) prevBtn.disabled = true;
              if (nextBtn) {
                var count = panel.querySelectorAll('.ub-ds__card').length;
                nextBtn.disabled = count <= 1;
              }
            });
          }, 0);
        });
      });
    });
  }

  // ============================================
  // FEATURES SCROLLING — hosted video autoplay (mobile / iOS)
  // ============================================
  function initFeaturesScrollVideo() {
    document.querySelectorAll('.ub-features-scroll__video-frame').forEach(function (frame) {
      if (frame.getAttribute('data-ub-feat-init') === '1') return;
      if (frame.querySelector('.ub-features-scroll__iframe')) {
        frame.setAttribute('data-ub-feat-init', '1');
        return;
      }
      var featRoot = frame.closest('.ub-features-scroll');
      if (!featRoot || !featRoot.hasAttribute('data-ub-features-scroll-autoplay')) {
        frame.setAttribute('data-ub-feat-init', '1');
        return;
      }
      frame.setAttribute('data-ub-feat-init', '1');

      function hideDeferredChrome(dm) {
        if (!dm) return;
        dm.querySelectorAll('.deferred-media__poster, button.deferred-media__poster').forEach(function (el) {
          el.setAttribute('aria-hidden', 'true');
          el.style.display = 'none';
          el.style.pointerEvents = 'none';
          el.style.visibility = 'hidden';
        });
        dm.querySelectorAll('.deferred-media__poster-button').forEach(function (el) {
          el.style.display = 'none';
        });
      }

      // Shopify video_tag often wraps hosted video in <deferred-media> with <video>
      // inside <template> until the poster is clicked. We inject the <video> early,
      // hide the poster UI, and call play() so mobile gets muted inline autoplay.
      function getOrLoadVideo() {
        var dm = frame.querySelector('deferred-media');
        var direct = frame.querySelector(':scope > video, video.ub-features-scroll__video-el');
        if (direct && (!dm || dm.contains(direct))) {
          hideDeferredChrome(dm);
          return direct;
        }
        if (!dm) return frame.querySelector('video');
        hideDeferredChrome(dm);
        var existing = dm.querySelector('video');
        if (existing && !dm.querySelector('template')) return existing;
        var tmpl = dm.querySelector('template');
        if (!tmpl) return dm.querySelector('video');
        var tmplVid = tmpl.content && tmpl.content.querySelector('video');
        if (!tmplVid) return null;
        tmplVid.setAttribute('muted', '');
        tmplVid.setAttribute('playsinline', '');
        tmplVid.setAttribute('webkit-playsinline', '');
        tmplVid.setAttribute('autoplay', '');
        tmplVid.setAttribute('loop', '');
        tmplVid.removeAttribute('controls');
        var clone = tmplVid.cloneNode(true);
        clone.muted = true;
        clone.defaultMuted = true;
        clone.loop = true;
        try {
          clone.playsInline = true;
        } catch (e1) {}
        dm.setAttribute('loaded', 'true');
        dm.appendChild(clone);
        hideDeferredChrome(dm);
        return clone;
      }

      var inViewport = false;

      function activate() {
        var vid = getOrLoadVideo();
        if (!vid || vid.getAttribute('data-ub-feat-video-init') === '1') return;
        vid.setAttribute('data-ub-feat-video-init', '1');
        vid.muted = true;
        vid.defaultMuted = true;
        vid.loop = true;
        vid.removeAttribute('controls');
        vid.setAttribute('autoplay', '');
        vid.setAttribute('loop', '');
        vid.setAttribute('muted', '');
        vid.setAttribute('playsinline', '');
        vid.setAttribute('webkit-playsinline', '');
        try {
          vid.playsInline = true;
        } catch (e0) {}

        function tryPlay() {
          var p = vid.play();
          if (p && p.catch) p.catch(function () {});
        }

        vid.addEventListener('pause', function () {
          if (!inViewport) return;
          window.requestAnimationFrame(function () {
            if (inViewport && vid.paused) tryPlay();
          });
        });

        vid.addEventListener('loadeddata', tryPlay, { once: true });
        vid.addEventListener('canplay', tryPlay, { once: true });
        if (vid.readyState >= 2) window.requestAnimationFrame(tryPlay);
        tryPlay();
      }

      if (typeof IntersectionObserver !== 'undefined') {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            inViewport = entry.isIntersecting;
            if (entry.isIntersecting) activate();
          });
        }, { threshold: 0.06, rootMargin: '80px 0px' });
        io.observe(frame);
      } else {
        inViewport = true;
        activate();
      }
    });
  }

  // ============================================
  // VIDEO REVIEWS — bottom editorial prev / next
  // ============================================
  function initVideoReviewsGrid(root) {
    function collapseExcept(keep) {
      root.querySelectorAll('.ub-video-card--expanded').forEach(function (c) {
        if (c !== keep) {
          c.classList.remove('ub-video-card--expanded');
        }
      });
      if (typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }
    }

    root.querySelectorAll('.ub-video-card').forEach(function (card) {
      var dm = card.querySelector('deferred-media');
      var poster =
        dm &&
        (dm.querySelector('[id^="Deferred-Poster-"]') || dm.querySelector('button.deferred-media__poster'));
      if (poster) {
        poster.addEventListener(
          'click',
          function (e) {
            if (card.classList.contains('ub-video-card--expanded')) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            collapseExcept(card);
            card.classList.add('ub-video-card--expanded');
            if (typeof dm.loadContent === 'function') {
              dm.loadContent();
            }
          },
          true
        );
      }
    });

    root.addEventListener('click', function (e) {
      var card = e.target.closest('.ub-video-card');
      if (!card || !root.contains(card)) return;
      if (card.querySelector('deferred-media')) return;
      if (!e.target.closest('.ub-video-thumbnail--static')) return;
      if (card.classList.contains('ub-video-card--expanded')) return;
      e.preventDefault();
      collapseExcept(card);
      card.classList.add('ub-video-card--expanded');
    });
  }

  function initVideoReviewsCarousel() {
    document.querySelectorAll('[data-ub-video-reviews-carousel]').forEach(function (root) {
      if (root.getAttribute('data-ub-video-reviews-init') === '1') return;

      if (root.classList.contains('ub-video-reviews--layout-grid')) {
        root.setAttribute('data-ub-video-reviews-init', '1');
        initVideoReviewsGrid(root);
        return;
      }

      var viewport = root.querySelector('[data-ub-video-carousel-viewport]');
      var prevBtn = root.querySelector('[data-ub-video-reviews-nav="prev"]');
      var nextBtn = root.querySelector('[data-ub-video-reviews-nav="next"]');
      if (!viewport || !prevBtn || !nextBtn) return;

      root.setAttribute('data-ub-video-reviews-init', '1');

      var gap = parseFloat(getComputedStyle(viewport.querySelector('.ub-video-grid') || viewport).getPropertyValue('--ub-vr-carousel-gap') || '0') || 12;
      var visible = parseFloat(viewport.style.getPropertyValue('--ub-vr-carousel-visible') || '1') || 1;

      function setCardWidth() {
        var w = viewport.clientWidth;
        if (!w) return;
        var cardW = (w - (visible - 1) * gap) / visible;
        viewport.style.setProperty('--ub-vr-card-w', cardW + 'px');
      }

      setCardWidth();
      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(function () {
          window.requestAnimationFrame(setCardWidth);
        });
        ro.observe(viewport);
      }
      window.addEventListener('resize', debounce(setCardWidth, 80));

      function cells() {
        return viewport.querySelectorAll('.ub-video-card');
      }

      function currentIndex() {
        var list = cells();
        if (!list.length) return 0;
        var sl = viewport.scrollLeft;
        var best = 0;
        for (var i = 0; i < list.length; i++) {
          if (list[i].offsetLeft <= sl + 8) best = i;
          else break;
        }
        return best;
      }

      function updateNav() {
        var max = viewport.scrollWidth - viewport.clientWidth;
        var sl = viewport.scrollLeft;
        prevBtn.disabled = sl <= 1;
        nextBtn.disabled = max <= 1 || sl >= max - 1;
      }

      function go(delta) {
        var list = cells();
        if (!list.length) return;
        var i = Math.min(list.length - 1, Math.max(0, currentIndex() + delta));
        var el = list[i];
        viewport.scrollTo({
          left: el.offsetLeft,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
        window.setTimeout(updateNav, prefersReducedMotion ? 0 : 320);
      }

      prevBtn.addEventListener('click', function () {
        go(-1);
      });
      nextBtn.addEventListener('click', function () {
        go(1);
      });
      viewport.addEventListener('scroll', debounce(updateNav, 48), { passive: true });
      window.addEventListener('resize', debounce(updateNav, 120));
      updateNav();
    });
  }

  // ============================================
  // HORIZONTAL SCROLL DRAG
  // ============================================
  function initHorizontalDrag() {
    var scrollers = document.querySelectorAll(
      '.ub-products-scroll, .ub-video-scroll:not(.ub-video-scroll--grid), .ub-pricing-cards-viewport--scroll'
    );
    scrollers.forEach(function(scroller) {
      if (scroller.getAttribute('data-ub-hdrag-init') === '1') return;
      scroller.setAttribute('data-ub-hdrag-init', '1');
      var isDown = false;
      var startX;
      var scrollLeft;

      scroller.addEventListener('mousedown', function(e) {
        isDown = true;
        scroller.style.cursor = 'grabbing';
        startX = e.pageX - scroller.offsetLeft;
        scrollLeft = scroller.scrollLeft;
      });

      scroller.addEventListener('mouseleave', function() {
        isDown = false;
        scroller.style.cursor = 'grab';
      });

      scroller.addEventListener('mouseup', function() {
        isDown = false;
        scroller.style.cursor = 'grab';
      });

      scroller.addEventListener('mousemove', function(e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - scroller.offsetLeft;
        var walk = (x - startX) * 1.5;
        scroller.scrollLeft = scrollLeft - walk;
      });

      scroller.style.cursor = 'grab';
    });
  }

  // ============================================
  // SMOOTH SCROLL LINKS
  // ============================================
  function initSmoothScroll() {
    if (typeof gsap === 'undefined' || typeof ScrollToPlugin === 'undefined') return;

    document.querySelectorAll('a[href^="#"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        var href = link.getAttribute('href');
        if (!href || href === '#' || href.length < 2) return;
        var target;
        try {
          target = document.querySelector(href);
        } catch (err) {
          return;
        }
        if (!target) return;
        e.preventDefault();
        gsap.to(window, {
          scrollTo: { y: target, offsetY: 80 },
          duration: 1,
          ease: 'power2.inOut',
        });
      });
    });
  }

  // ============================================
  // CART DRAWER — reservation timer + discount apply + count sync
  // ============================================
  var ubCartTimerIntervalId = null;
  var UB_CART_RESERVE_KEY = 'ub_cart_reserve_until';
  var ubCartCountSyncSubscribed = false;

  function clearUbCartTimerInterval() {
    if (ubCartTimerIntervalId !== null) {
      clearInterval(ubCartTimerIntervalId);
      ubCartTimerIntervalId = null;
    }
  }

  function initUbCartTimer() {
    clearUbCartTimerInterval();
    var root = document.querySelector('[data-ub-cart-timer]');
    if (!root) return;
    var display = root.querySelector('[data-ub-cart-timer-display]');
    if (!display) return;

    var minutes = parseInt(root.getAttribute('data-minutes'), 10);
    if (!minutes || minutes < 1) minutes = 8;

    function ensureEndTime() {
      var now = Date.now();
      var raw;
      try {
        raw = sessionStorage.getItem(UB_CART_RESERVE_KEY);
      } catch (e) {
        raw = null;
      }
      var end = raw ? parseInt(raw, 10) : 0;
      if (!end || end < now) {
        end = now + minutes * 60 * 1000;
        try {
          sessionStorage.setItem(UB_CART_RESERVE_KEY, String(end));
        } catch (err) {}
      }
      return end;
    }

    var endTime = ensureEndTime();

    function tick() {
      var ms = endTime - Date.now();
      if (ms <= 0) {
        endTime = Date.now() + minutes * 60 * 1000;
        try {
          sessionStorage.setItem(UB_CART_RESERVE_KEY, String(endTime));
        } catch (e) {}
        ms = endTime - Date.now();
      }
      var totalSec = Math.max(0, Math.floor(ms / 1000));
      var m = Math.floor(totalSec / 60);
      var sec = totalSec % 60;
      display.textContent =
        (m < 10 ? '0' : '') + m + ':' + (sec < 10 ? '0' : '') + sec;
    }

    tick();
    ubCartTimerIntervalId = setInterval(tick, 1000);
  }

  function initUbCartDiscountApply() {
    var btn = document.querySelector('[data-ub-cart-discount-apply]');
    var input = document.querySelector('[data-ub-cart-discount-input]');
    if (!btn || !input || btn.getAttribute('data-ub-discount-init') === '1') return;
    btn.setAttribute('data-ub-discount-init', '1');

    function apply() {
      var code = String(input.value || '').trim();
      if (!code) return;
      var path = window.location && window.location.pathname ? window.location.pathname : '/';
      window.location.href =
        '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(path);
    }

    btn.addEventListener('click', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        apply();
      }
    });
  }

  function initUbCartCountAndReserveSync() {
    if (ubCartCountSyncSubscribed) return;
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    function onCartUpdate(event) {
      if (!event || !event.cartData) return;
      var n = event.cartData.item_count;
      if (typeof n !== 'number') return;
      document.querySelectorAll('[data-ub-cart-count]').forEach(function (el) {
        el.textContent = String(n);
      });
      if (n === 0) {
        try {
          sessionStorage.removeItem(UB_CART_RESERVE_KEY);
        } catch (e) {}
      }
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    ubCartCountSyncSubscribed = true;
  }

  function initUbCartDrawerEnhancements() {
    initUbCartTimer();
    initUbCartDiscountApply();
    initUbCartCountAndReserveSync();
  }

  // ============================================
  // HERO — 3D perspective wave grid (canvas)
  // ============================================
  function initHeroWaveCanvas() {
    return; // Fast mode: canvas wave disabled
    var wrap = document.querySelector('[data-ub-hero-wave]');
    if (!wrap || wrap.getAttribute('data-ub-wave-init') === '1') return;
    if (prefersReducedMotion) {
      wrap.style.visibility = 'hidden';
      return;
    }

    var canvas = wrap.querySelector('canvas');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    wrap.setAttribute('data-ub-wave-init', '1');
    ensureWaveMouseTracking();

    var hero = wrap.closest('.ub-hero');
    var t = 0;
    var rafId;
    var cachedColors = null;
    var cols = 28;
    var rows = 22;
    /* Wider / deeper world so perspective rays fill the canvas edge-to-edge */
    var xMin = -2.35;
    var xMax = 2.35;
    var zMin = 0.12;
    var zMax = 3.35;

    function waveY(x, z, time) {
      var mx = ubWaveMouse.x;
      var my = ubWaveMouse.y;
      /* Low amplitude + slow phase — background texture, not a focal effect */
      return (
        0.07 * Math.sin(x * 3.4 + time * 0.16) * Math.cos(z * 2.9 + time * 0.13) +
        0.035 * Math.sin((x + z * 0.72) * 4.8 - time * 0.28) +
        0.012 * mx * Math.sin(z * 4.2 + time * 0.07) +
        0.008 * my * Math.cos(x * 3.6 + time * 0.08)
      );
    }

    function project(x, y, z, w, h) {
      var camZ = 0.07;
      var inv = 1 / (z + camZ);
      /* Full-bleed: large scale + separate vertical weight so the mesh isn’t a tight band in the middle */
      var scale = Math.max(w, h) * 0.82;
      var px = x * inv * scale + w * 0.5;
      var py = h * 0.02 + (0.32 + y * 1.45) * inv * scale * 1.18;
      return [px, py];
    }

    function getColors() {
      if (cachedColors) return cachedColors;
      var cs = hero ? getComputedStyle(hero) : null;
      var bg = cs && cs.getPropertyValue('--ub-wave-bg').trim();
      var line = cs && cs.getPropertyValue('--ub-wave-line').trim();
      if (!bg) bg = '#ffffff';
      if (!line) line = '#0a0a0a';
      var alpha = parseFloat(wrap.getAttribute('data-wave-opacity'));
      if (isNaN(alpha)) alpha = 0.09;
      cachedColors = { bg: bg, line: line, alpha: alpha };
      return cachedColors;
    }

    function drawFrame() {
      var rect = wrap.getBoundingClientRect();
      var w = rect.width;
      var h = rect.height;
      if (w < 2 || h < 2) return;

      var colors = getColors();
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = colors.alpha;
      ctx.strokeStyle = colors.line;
      ctx.lineWidth = 0.85;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      var i;
      var j;
      var p;
      var first;

      for (i = 0; i <= cols; i++) {
        var x = xMin + ((xMax - xMin) * i) / cols;
        ctx.beginPath();
        first = true;
        for (j = 0; j <= rows; j++) {
          var z = zMin + ((zMax - zMin) * j) / rows;
          var y = waveY(x, z, t);
          p = project(x, y, z, w, h);
          if (first) {
            ctx.moveTo(p[0], p[1]);
            first = false;
          } else {
            ctx.lineTo(p[0], p[1]);
          }
        }
        ctx.stroke();
      }

      for (j = 0; j <= rows; j++) {
        var zRow = zMin + ((zMax - zMin) * j) / rows;
        ctx.beginPath();
        first = true;
        for (i = 0; i <= cols; i++) {
          var xRow = xMin + ((xMax - xMin) * i) / cols;
          var yRow = waveY(xRow, zRow, t);
          p = project(xRow, yRow, zRow, w, h);
          if (first) {
            ctx.moveTo(p[0], p[1]);
            first = false;
          } else {
            ctx.lineTo(p[0], p[1]);
          }
        }
        ctx.stroke();
      }

      ctx.restore();
      t += 0.002;
    }

    function tick() {
      drawFrame();
      rafId = window.requestAnimationFrame(tick);
    }

    function startWave() {
      if (!rafId) rafId = window.requestAnimationFrame(tick);
    }

    function stopWave() {
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
    }

    function resize() {
      var rect = wrap.getBoundingClientRect();
      var w = rect.width;
      var h = rect.height;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }

    resize();

    if (typeof IntersectionObserver !== 'undefined') {
      var waveIO = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) { startWave(); } else { stopWave(); }
        });
      }, { threshold: 0.01 });
      waveIO.observe(wrap);
    } else {
      startWave();
    }

    var ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(function () {
        cachedColors = null;
        resize();
      });
      ro.observe(wrap);
    } else {
      window.addEventListener('resize', debounce(function() { cachedColors = null; resize(); }, 100));
    }
  }

  // ============================================
  // LIVE DEMO — tabbed scrollable screenshots (no iframe)
  // ============================================
  function initUbLiveDemoTabs() {
    document.querySelectorAll('[data-ub-live-demo-tabs]').forEach(function (root) {
      if (root.getAttribute('data-ub-ld-init') === '1') return;
      var tabs = root.querySelectorAll('[data-ub-ld-tab]');
      var panels = root.querySelectorAll('[data-ub-ld-panel]');
      if (!tabs.length || !panels.length) return;

      root.setAttribute('data-ub-ld-init', '1');
      var activeTabId = null;
      var pausedByClick = false;
      var pausedByInteraction = false;
      var inViewport = true;
      var autoSpeed = parseFloat(root.getAttribute('data-auto-scroll-pps') || '22');
      if (!isFinite(autoSpeed) || autoSpeed <= 0) autoSpeed = 22;
      var rafId = null;
      var lastTs = 0;
      if (typeof IntersectionObserver !== 'undefined') {
        var demoIO = new IntersectionObserver(function(entries) {
          entries.forEach(function(e) { inViewport = e.isIntersecting; });
        }, { threshold: 0.01 });
        demoIO.observe(root);
      }

      function activate(tabId) {
        activeTabId = tabId;
        tabs.forEach(function (btn) {
          var id = btn.getAttribute('data-ub-ld-tab');
          var on = id === tabId;
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
          btn.classList.toggle('ub-live-demo-tabs__btn--active', on);
          btn.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (panel) {
          var id = panel.getAttribute('data-ub-ld-panel');
          if (id === tabId) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });
      }

      function getActiveScroller() {
        if (!activeTabId) return null;
        var panel = root.querySelector('[data-ub-ld-panel="' + activeTabId + '"]');
        if (!panel || panel.hasAttribute('hidden')) return null;
        return panel.querySelector('[data-ub-ld-autoscroll]');
      }

      function tick(ts) {
        if (!lastTs) lastTs = ts;
        var delta = (ts - lastTs) / 1000;
        lastTs = ts;
        var scroller = getActiveScroller();
        if (scroller && !pausedByClick && !pausedByInteraction && inViewport) {
          var maxScroll = scroller.scrollHeight - scroller.clientHeight;
          if (maxScroll > 2) {
            var blockSpeed = parseFloat(scroller.getAttribute('data-ub-ld-autoscroll-pps'));
            var speed = (isFinite(blockSpeed) && blockSpeed > 0) ? blockSpeed : autoSpeed;
            var next = scroller.scrollTop + speed * delta;
            if (next >= maxScroll) {
              scroller.scrollTop = 0;
            } else {
              scroller.scrollTop = next;
            }
          }
        }
        rafId = window.requestAnimationFrame(tick);
      }

      function pauseAutoscrollByClick() {
        pausedByClick = true;
        root.setAttribute('data-ub-ld-paused', 'true');
      }

      function resumeAutoscroll() {
        pausedByClick = false;
        root.removeAttribute('data-ub-ld-paused');
      }

      var start = root.getAttribute('data-default-tab');
      if (!start || !root.querySelector('[data-ub-ld-tab="' + start + '"]')) {
        start = tabs[0].getAttribute('data-ub-ld-tab');
      }
      activate(start);
      rafId = window.requestAnimationFrame(tick);

      tabs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          activate(btn.getAttribute('data-ub-ld-tab'));
        });
        btn.addEventListener('keydown', function (e) {
          var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (keys.indexOf(e.key) === -1) return;
          e.preventDefault();
          var i = Array.prototype.indexOf.call(tabs, document.activeElement);
          if (i < 0) i = 0;
          if (e.key === 'Home') {
            tabs[0].focus();
            activate(tabs[0].getAttribute('data-ub-ld-tab'));
            return;
          }
          if (e.key === 'End') {
            tabs[tabs.length - 1].focus();
            activate(tabs[tabs.length - 1].getAttribute('data-ub-ld-tab'));
            return;
          }
          var next = e.key === 'ArrowRight' ? Math.min(tabs.length - 1, i + 1) : Math.max(0, i - 1);
          tabs[next].focus();
          activate(tabs[next].getAttribute('data-ub-ld-tab'));
        });
      });

      root.querySelectorAll('[data-ub-ld-autoscroll]').forEach(function (scroller) {
        scroller.addEventListener('click', function (e) {
          if (e.target && e.target.closest('img, picture, .ub-live-demo-scroll__frame')) {
            pauseAutoscrollByClick();
          }
        });
        scroller.addEventListener('mouseenter', function () {
          pausedByInteraction = true;
        });
        scroller.addEventListener('mouseleave', function () {
          pausedByInteraction = false;
        });
        scroller.addEventListener('focusin', function () {
          pausedByInteraction = true;
        });
        scroller.addEventListener('focusout', function () {
          pausedByInteraction = false;
        });
        scroller.addEventListener('touchstart', function () {
          pausedByInteraction = true;
        }, { passive: true });
        scroller.addEventListener('touchend', function () {
          pausedByInteraction = false;
        }, { passive: true });
        scroller.addEventListener('wheel', function () {
          pausedByInteraction = true;
          window.clearTimeout(scroller.__ubLdWheelT);
          scroller.__ubLdWheelT = window.setTimeout(function () {
            pausedByInteraction = false;
          }, 500);
        }, { passive: true });
      });

      root.addEventListener('dblclick', function () {
        resumeAutoscroll();
      });

      root.addEventListener('remove', function () {
        if (rafId) window.cancelAnimationFrame(rafId);
      });
    });
  }

  // ============================================
  // PREFETCH — hover-based page prefetching for instant navigation
  // ============================================
  function initPrefetch() {
    var prefetched = new Set();
    var origin = window.location.origin;
    var hoverTimer = null;

    function canPrefetch(href) {
      if (!href) return false;
      try {
        var u = new URL(href, origin);
        if (u.origin !== origin) return false;
        // Hash-only links don't need a fetch
        if (!u.pathname && u.hash) return false;
        var p = u.pathname;
        if (/\/(cart\/(add|change|clear)|admin|cdn\/)/.test(p)) return false;
        return true;
      } catch (e) { return false; }
    }

    function prefetchUrl(href) {
      if (prefetched.has(href)) return;
      prefetched.add(href);
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = href;
      document.head.appendChild(link);
    }

    function warmCheckout() {
      if (document.querySelector('link[href*="checkout.shopify.com"][rel="preconnect"]')) return;
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = 'https://checkout.shopify.com';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    // Desktop: 80ms delay so casual mouse-passes don't fire
    document.addEventListener('mouseover', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      var href = a.href;
      if (!canPrefetch(href)) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { prefetchUrl(href); }, 30);
    }, { passive: true });

    document.addEventListener('mouseout', function () {
      clearTimeout(hoverTimer);
    }, { passive: true });

    // Mobile: prefetch on touchstart (user has committed to the tap)
    document.addEventListener('touchstart', function (e) {
      var a = e.target.closest('a[href]');
      if (!a || !canPrefetch(a.href)) return;
      prefetchUrl(a.href);
    }, { passive: true });

    // Checkout: warm up TCP/TLS to Shopify checkout on hover or open cart
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest('#CartDrawer-Checkout, [name="checkout"]')) warmCheckout();
    }, { passive: true });

    document.addEventListener('touchstart', function (e) {
      if (e.target.closest('#CartDrawer-Checkout, [name="checkout"]')) warmCheckout();
    }, { passive: true });
  }

  // ============================================
  // INIT
  // ============================================
  function init() {
    initGSAP();
    initMarquees();
    initUbLiveDemoTabs();

    var loaderMode = document.documentElement.getAttribute('data-ub-loader') || 'fullscreen';
    if (loaderMode === 'fade') {
      initPageFade();
    } else {
      initLoader();
      if (!document.getElementById('ub-loader')) {
        initAfterLoader();
      }
    }

    initFAQ();
    initMagneticButtons();
    initHorizontalDrag();
    initPricingCardsCarousel();
    initDemoSlideshowNav();
    initFeaturesScrollVideo();
    initVideoReviewsCarousel();
    initSmoothScroll();
    initThemePdpEnhancements();
    initUbPdpMediaGalleryDots();
    initPdpStickyCta();
    initUbCartDrawerEnhancements();
    initPrefetch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: marquee clone for newly injected sections (FAQ uses delegated clicks) */
  document.addEventListener('shopify:section:load', function (ev) {
    if (ev && ev.target) {
      ev.target.querySelectorAll('[data-ub-live-demo-tabs]').forEach(function (el) {
        el.removeAttribute('data-ub-ld-init');
      });
    }
    initMarquees();
    initUbLiveDemoTabs();
    initThemePdpEnhancements();
    initUbPdpMediaGalleryDots();
    initUbCartDrawerEnhancements();
    initPdpStickyCta();
    initHeroWaveCanvas();
    initHorizontalDrag();
    initPricingCardsCarousel();
    initDemoSlideshowNav();
    initFeaturesScrollVideo();
    initVideoReviewsCarousel();
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
    if (document.body.classList.contains('ub-template-product')) {
      clearScrollAnimationFallback();
    }
  });

  // ============================================
  // THEME PDP — live store iframe slideshow + install video
  // ============================================
  function initStoreShowcases() {
    document.querySelectorAll('.ub-store-showcase').forEach(function (root) {
      var sid = root.getAttribute('data-section-id');
      if (!sid) return;
      var dataEl = document.getElementById('ub-store-data-' + sid);
      if (!dataEl) return;
      var stores;
      try {
        stores = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      if (!stores || !stores.length) return;
      stores = stores.filter(function (s) {
        return s && s.url && String(s.url).trim() && s.url.indexOf('http') === 0;
      });
      if (!stores.length) return;

      var iframe = root.querySelector('.ub-store-showcase__iframe');
      var caption = root.querySelector('.ub-store-showcase__caption-text');
      var openLink = root.querySelector('.ub-store-showcase__open');
      var dotsWrap = root.querySelector('.ub-store-showcase__dots');
      var prevBtn = root.querySelector('.ub-store-showcase__btn--prev');
      var nextBtn = root.querySelector('.ub-store-showcase__btn--next');
      var idx = 0;

      function setSlide(i) {
        idx = (i + stores.length * 10) % stores.length;
        var s = stores[idx];
        if (iframe && s && s.url) {
          iframe.src = s.url;
        }
        if (caption) caption.textContent = (s && s.label) ? s.label : '';
        if (openLink && s && s.url) {
          openLink.href = s.url;
          openLink.style.display = 'inline';
        }
        if (dotsWrap) {
          var dots = dotsWrap.querySelectorAll('.ub-store-showcase__dot');
          dots.forEach(function (d, di) {
            d.classList.toggle('is-active', di === idx);
            d.setAttribute('aria-selected', di === idx ? 'true' : 'false');
          });
        }
        root.setAttribute('data-active', String(idx));
      }

      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        stores.forEach(function (_, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ub-store-showcase__dot' + (i === 0 ? ' is-active' : '');
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
          b.setAttribute('aria-label', 'Store ' + (i + 1));
          b.addEventListener('click', function () {
            setSlide(i);
          });
          dotsWrap.appendChild(b);
        });
      }

      if (prevBtn) prevBtn.addEventListener('click', function () {
        setSlide(idx - 1);
      });
      if (nextBtn) nextBtn.addEventListener('click', function () {
        setSlide(idx + 1);
      });

      setSlide(0);
    });
  }

  function videoUrlToEmbed(url, autoplay) {
    if (!url || typeof url !== 'string') return '';
    url = url.trim();
    var ap = autoplay ? 1 : 0;
    if (url.indexOf('youtube.com/embed') !== -1) {
      return url.split('&')[0].split('?')[0] + '?rel=0&autoplay=' + ap;
    }
    var yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (yt) return 'https://www.youtube.com/embed/' + yt[1] + '?rel=0&autoplay=' + ap;
    var vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=' + ap;
    // Do not pass arbitrary URLs into iframe src (only YouTube/Vimeo embeds above).
    return '';
  }

  function initInstallVideos() {
    function ubFmtTime(sec) {
      sec = Number(sec || 0);
      if (!isFinite(sec) || sec < 0) sec = 0;
      var m = Math.floor(sec / 60);
      var s = Math.floor(sec % 60);
      return m + ':' + (s < 10 ? '0' + s : s);
    }

    document.querySelectorAll('.ub-install-video').forEach(function (root) {
      if (root.getAttribute('data-ub-video-init') === '1') return;
      var url = (root.getAttribute('data-video-url') || '').trim();
      var hosted = root.getAttribute('data-hosted-native') === '1';
      if (!hosted && !url) return;

      var inner = root.querySelector('.ub-install-video__inner');
      var embedWrap = root.querySelector('.ub-install-video__embed');
      var poster = root.querySelector('.ub-install-video__poster');
      var playBtns = root.querySelectorAll('[data-ub-video-toggle]');
      var playIcons = root.querySelectorAll('[data-ub-video-toggle-icon]');
      var progressFill = root.querySelector('[data-ub-video-progress]');
      var progressSeek = root.querySelector('[data-ub-video-seek]');
      var timeLabel = root.querySelector('[data-ub-video-time]');
      if (!inner || !embedWrap || !playBtns.length) return;

      root.setAttribute('data-ub-video-init', '1');

      var setPlayVisualState = function (isPlaying) {
        root.setAttribute('data-state', isPlaying ? 'playing' : 'paused');
        playBtns.forEach(function (btn) {
          btn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
          btn.setAttribute('data-state', isPlaying ? 'playing' : 'paused');
        });
        playIcons.forEach(function (icon) {
          if (icon) icon.textContent = isPlaying ? '❚❚' : '▶';
        });
      };

      var togglePlayback = function () {
        if (hosted) {
          embedWrap.hidden = false;
          if (poster) poster.style.display = 'none';
          var vid = embedWrap.querySelector('video');
          if (vid) {
            // Hard-disable native controls in case browser/shopify re-adds them.
            vid.controls = false;
            vid.removeAttribute('controls');
            var updateUi = function () {
              if (progressFill && vid.duration) {
                var pct = Math.min(100, Math.max(0, (vid.currentTime / vid.duration) * 100));
                progressFill.style.width = pct + '%';
              }
              if (timeLabel && vid.duration) {
                timeLabel.textContent = ubFmtTime(vid.currentTime) + ' / ' + ubFmtTime(vid.duration);
              }
            };
            if (!vid._ubInstallControlsBound) {
              vid.addEventListener('loadedmetadata', updateUi);
              vid.addEventListener('timeupdate', updateUi);
              vid.addEventListener('durationchange', updateUi);
              vid.addEventListener('playing', function () {
                setPlayVisualState(true);
                root.removeAttribute('data-loading');
              });
              vid.addEventListener('waiting', function () {
                root.setAttribute('data-loading', '1');
              });
              vid.addEventListener('stalled', function () {
                root.setAttribute('data-loading', '1');
                var r = vid.play();
                if (r && typeof r.catch === 'function') r.catch(function () {});
              });
              vid.addEventListener('canplay', function () {
                root.removeAttribute('data-loading');
              });
              vid.addEventListener('pause', function () {
                setPlayVisualState(false);
                root.removeAttribute('data-loading');
              });
              vid.addEventListener('ended', function () {
                setPlayVisualState(false);
                root.removeAttribute('data-loading');
              });
              vid.addEventListener('error', function () {
                setPlayVisualState(false);
                root.removeAttribute('data-loading');
              });
              if (progressSeek) {
                progressSeek.addEventListener('click', function (evt) {
                  if (!vid.duration) return;
                  var rect = progressSeek.getBoundingClientRect();
                  if (!rect.width) return;
                  var ratio = (evt.clientX - rect.left) / rect.width;
                  ratio = Math.max(0, Math.min(1, ratio));
                  vid.currentTime = ratio * vid.duration;
                });
              }
              vid._ubInstallControlsBound = true;
            }
            try {
              if (vid.paused) {
                var p = vid.play();
                if (p && typeof p.catch === 'function') p.catch(function () {});
              } else {
                vid.pause();
              }
            } catch (e) {}
            updateUi();
          }
          return;
        }
        var embedUrl = videoUrlToEmbed(url, true);
        if (!embedUrl) return;
        embedWrap.innerHTML =
          '<iframe class="ub-install-video__iframe" src="' +
          embedUrl.replace(/"/g, '&quot;') +
          '" title="Install video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe>';
        embedWrap.hidden = false;
        setPlayVisualState(true);
        if (poster) poster.style.display = 'none';
      };

      playBtns.forEach(function (btn) {
        btn.addEventListener('click', togglePlayback);
      });

      setPlayVisualState(false);
    });
  }

  // ============================================
  // PDP — luxury editorial dots (main product gallery)
  // ============================================
  var ubPdpDotsVariantSubscribed = false;

  function initUbPdpMediaGalleryDots() {
    document.querySelectorAll('[data-ub-pdp-media-dots]').forEach(function (dotsRoot) {
      var sliderComp = dotsRoot.closest('slider-component');
      if (!sliderComp) return;
      var slider = sliderComp.querySelector('ul[id^="Slider-Gallery"]');
      if (!slider) return;

      function getSlides() {
        return Array.prototype.slice.call(slider.querySelectorAll('li.slider__slide[id^="Slide-"]'));
      }

      function updateActive() {
        var slideList = getSlides();
        var btns = dotsRoot.querySelectorAll('.ub-pdp-media-dot');
        if (!slideList.length || !btns.length) return;
        // getBoundingClientRect is reliable in any layout; offsetLeft can be
        // wrong when slides are inside a flex/grid container
        var sliderRect = slider.getBoundingClientRect();
        var sliderMid = sliderRect.left + sliderRect.width / 2;
        var best = 0;
        var bestDist = Infinity;
        slideList.forEach(function (slide, j) {
          var r = slide.getBoundingClientRect();
          var dist = Math.abs((r.left + r.width / 2) - sliderMid);
          if (dist < bestDist) { bestDist = dist; best = j; }
        });
        btns.forEach(function (b, idx) {
          var on = idx === best;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-current', on ? 'true' : 'false');
          b.setAttribute('tabindex', on ? '0' : '-1');
        });
      }

      function rebuild() {
        dotsRoot.innerHTML = '';
        var slideList = getSlides();
        var dotsShell = dotsRoot.closest('.product__media-slider-dots');
        if (slideList.length < 2) {
          if (dotsShell) dotsShell.classList.add('hidden');
          return;
        }
        if (dotsShell) dotsShell.classList.remove('hidden');
        slideList.forEach(function (slide, i) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ub-pdp-media-dot';
          btn.setAttribute('role', 'tab');
          btn.setAttribute(
            'aria-label',
            'Slide ' + (i + 1) + ' of ' + slideList.length
          );
          var mark = document.createElement('span');
          mark.className = 'ub-pdp-media-dot__mark';
          mark.setAttribute('aria-hidden', 'true');
          btn.appendChild(mark);
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            slider.scrollTo({
              left: slide.offsetLeft,
              behavior: prefersReducedMotion ? 'auto' : 'smooth',
            });
          });
          dotsRoot.appendChild(btn);
        });
        updateActive();
      }

      dotsRoot._ubRebuildPdpDots = rebuild;

      if (!dotsRoot.getAttribute('data-ub-pdp-dots-wired')) {
        dotsRoot.setAttribute('data-ub-pdp-dots-wired', '1');
        // rAF-throttled scroll: dots update every frame while swiping
        var rafScheduled = false;
        slider.addEventListener('scroll', function () {
          if (rafScheduled) return;
          rafScheduled = true;
          requestAnimationFrame(function () { rafScheduled = false; updateActive(); });
        }, { passive: true });
        // scrollend fires once the snap animation fully settles (Chrome 114+, FF 109+)
        slider.addEventListener('scrollend', updateActive, { passive: true });
        window.addEventListener('resize', debounce(updateActive, 120));
      }

      rebuild();
    });

    if (!ubPdpDotsVariantSubscribed && typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      ubPdpDotsVariantSubscribed = true;
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            initUbPdpMediaGalleryDots();
          });
        });
      });
    }
  }

  function initThemePdpEnhancements() {
    initStoreShowcases();
    initInstallVideos();
  }

  // ============================================
  // PRODUCT PDP — sticky buy bar (sync price + trigger main ATC)
  // ============================================
  var ubPdpStickyGlobalsWired = false;

  function syncUbPdpStickyPrice() {
    var src = document.querySelector('product-info .price');
    var dst = document.getElementById('ub-pdp-sticky-price-mount');
    if (!src || !dst) return;
    dst.innerHTML = src.innerHTML;
    dst.querySelectorAll('.badge').forEach(function (el) {
      el.remove();
    });
  }

  function ubFindMainAddToCartButton(productInfo) {
    if (!productInfo) return null;
    var sid = productInfo.dataset.section;
    if (sid) {
      var byId = document.getElementById('ProductSubmitButton-' + sid);
      if (byId) return byId;
    }
    return (
      productInfo.querySelector('form[data-type="add-to-cart-form"] button[type="submit"][name="add"]') ||
      productInfo.querySelector('button[type="submit"][name="add"]')
    );
  }

  function syncUbPdpStickyButtonState() {
    var pi = document.querySelector('product-info');
    var stickyBtn = document.getElementById('ub-pdp-sticky-add');
    if (!pi || !stickyBtn) return;
    var mainBtn = ubFindMainAddToCartButton(pi);
    if (!mainBtn) return;
    stickyBtn.disabled = !!(mainBtn.disabled || mainBtn.hasAttribute('disabled'));
  }

  function initPdpStickyCta() {
    var root = document.getElementById('ub-pdp-sticky');
    if (!root) return;
    var stickyBtn = document.getElementById('ub-pdp-sticky-add');
    var pi = document.querySelector('product-info');
    if (!pi || !stickyBtn) return;
    if (stickyBtn.getAttribute('data-ub-sticky-bound') === '1') {
      syncUbPdpStickyPrice();
      syncUbPdpStickyButtonState();
      return;
    }

    function bindStickyClick() {
      if (stickyBtn.getAttribute('data-ub-sticky-bound') === '1') return true;
      var mainBtn = ubFindMainAddToCartButton(pi);
      if (!mainBtn) return false;
      stickyBtn.addEventListener('click', function () {
        mainBtn.click();
      });
      stickyBtn.setAttribute('data-ub-sticky-bound', '1');
      return true;
    }

    if (!bindStickyClick()) {
      var onPiReady = function () {
        if (bindStickyClick()) {
          pi.removeEventListener('product-info:loaded', onPiReady);
        }
      };
      pi.addEventListener('product-info:loaded', onPiReady);
      window.setTimeout(function () {
        if (bindStickyClick()) {
          pi.removeEventListener('product-info:loaded', onPiReady);
        }
      }, 0);
    }

    syncUbPdpStickyPrice();
    syncUbPdpStickyButtonState();

    if (!ubPdpStickyGlobalsWired) {
      ubPdpStickyGlobalsWired = true;
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function () {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              syncUbPdpStickyPrice();
              syncUbPdpStickyButtonState();
            });
          });
        });
      }
      window.addEventListener(
        'load',
        function () {
          syncUbPdpStickyPrice();
          syncUbPdpStickyButtonState();
        },
        { once: true }
      );
    }
  }

})();
